import { graphql, navigate, useStaticQuery } from "gatsby"
import React, { useEffect, useState } from "react"
import Form from "react-bootstrap/Form"
import { useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { gql, useMutation } from "@apollo/client"
import { SiteContext } from "@context/Provider"
import Button from "@knowit-alma/common/src/components/atoms/Button/Button"
import ModalWindow from "@knowit-alma/common/src/components/atoms/ModalWindow/ModalWindow"
import FormFieldCheckbox from "@knowit-alma/common/src/components/molecules/FormFieldCheckbox/FormFieldCheckbox"
import FormFieldFileUpload from "@knowit-alma/common/src/components/molecules/FormFieldFileUpload/FormFieldFileUpload"
import FormFieldRadio from "@knowit-alma/common/src/components/molecules/FormFieldRadio/FormFieldRadio"
import FormFieldSelect from "@knowit-alma/common/src/components/molecules/FormFieldSelect/FormFieldSelect"
import FormFieldText from "@knowit-alma/common/src/components/molecules/FormFieldText/FormFieldText"
import FormFieldTextArea from "@knowit-alma/common/src/components/molecules/FormFieldTextArea/FormFieldTextArea"
import "@knowit-alma/common/src/lang/i18n"

const SUBMIT_FORM = gql`
  mutation submit($id: ID!, $fieldValues: [FormFieldValuesInput] = []) {
    submitGfForm(
      input: {
        id: $id
        clientMutationId: "submitGfForm"
        fieldValues: $fieldValues
      }
    ) {
      errors {
        id # The field that failed validation.
        message
      }
      clientMutationId
      entry {
        id
      }
    }
  }
`

const GravityFormsForm = ({ formId }) => {
  const { t } = useTranslation()

  // Form data

  const allForms = useStaticQuery(
    graphql`
      {
        data: allWpGfForm {
          edges {
            node {
              id
              databaseId
              confirmations {
                id
                message
                type
                url
              }
              formFields {
                nodes {
                  ... on WpHtmlField {
                    id
                    content
                    type
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpEmailField {
                    id
                    description
                    errorMessage
                    isRequired
                    label
                    placeholder
                    type
                    visibility
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpCaptchaField {
                    id
                    captchaType
                    captchaTheme
                    captchaLanguage
                    label
                    description
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpTextField {
                    id
                    defaultValue
                    description
                    errorMessage
                    isRequired
                    label
                    maxLength
                    placeholder
                    type
                    visibility
                    defaultValue
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpEmailField {
                    id
                    adminLabel
                    description
                    errorMessage
                    isRequired
                    label
                    type
                    placeholder
                    visibility
                    value
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpNumberField {
                    id
                    type
                    defaultValue
                    description
                    errorMessage
                    isRequired
                    label
                    placeholder
                    visibility
                    value
                    rangeMin
                    rangeMax
                    numberFormat
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpTextAreaField {
                    id
                    label
                    placeholder
                    description
                    maxLength
                    errorMessage
                    isRequired
                    type
                    visibility
                    defaultValue
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpFileUploadField {
                    id
                    description
                    errorMessage
                    isRequired
                    label
                    maxFileSize
                    maxFiles
                    canAcceptMultipleFiles
                    type
                    visibility
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpRadioField {
                    type
                    id
                    description
                    errorMessage
                    isRequired
                    label
                    visibility
                    choices {
                      text
                      isSelected
                      value
                    }
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpCheckboxField {
                    type
                    id
                    description
                    errorMessage
                    isRequired
                    label
                    visibility
                    choices {
                      isSelected
                      text
                      value
                    }
                    checkboxValues {
                      inputId
                      value
                    }
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                  ... on WpSelectField {
                    id
                    type
                    defaultValue
                    description
                    errorMessage
                    isRequired
                    label
                    placeholder
                    visibility
                    choices {
                      value
                      text
                      isSelected
                    }
                    conditionalLogic {
                      actionType
                      logicType
                      rules {
                        fieldId
                        operator
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `
  )

  const forms = allForms?.data?.edges
    ?.filter((form) => {
      return form?.node?.databaseId === formId
    })
    .map((form) => {
      return form.node
    })
    .filter(Boolean)

  const form = forms.length > 0 ? forms[0] : null

  const confirmation = form?.confirmations[0]

  // useForm

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    //resolver: superstructResolver(validationSchema),
  })

  // Conditional logic

  const watch = useWatch({ control, defaultValue: {} })
  const [initialValues, setInitialValues] = useState({})

  useEffect(() => {
    setInitialValues(getValues())
  }, [getValues])

  // Form submission

  const [serverState, setServerState] = useState({
    submitting: false,
    status: null,
  })
  const [formData, setFormData] = useState()

  const [mutateFunction] = useMutation(SUBMIT_FORM, {
    variables: { id: formId, fieldValues: formData },
    onCompleted(response) {
      setServerState({ submitting: false })
      if (response?.submitGfForm?.entry?.id) {
        reset()
        if (confirmation) {
          if (confirmation.type === "MESSAGE") {
            setSuccessModalVisible(true)
          }
          if (confirmation.type === "REDIRECT") {
            navigate(confirmation.url)
          }
        }
      } else {
        setErrorModalVisible(true)
      }
    },
    onError(response) {
      setErrorModalVisible(true)
      setServerState({ submitting: false })
    },
  })

  const onSubmit = (data) => {
    setServerState({ submitting: true })

    let formattedData = []

    Object.entries(data).forEach(([key, value]) => {
      const fieldId = parseInt(key.replace(`field_${formId}_`, ""))
      const fieldObject = form?.formFields?.nodes.filter((field) => {
        return field.id === fieldId
      })[0]

      switch (fieldObject?.type) {
        case "number":
        case "text":
        case "textarea":
          formattedData.push({
            id: fieldId,
            value: value,
          })
          break
        case "select":
        case "radio":
          if (value) {
            formattedData.push({
              id: fieldId,
              value: value,
            })
          }
          break
        case "fileupload":
          formattedData.push({
            id: fieldId,
            fileUploadValues: value,
          })
          break
        case "email":
          value = { value }
          formattedData.push({
            id: fieldId,
            emailValues: value,
          })
          break
        case "checkbox":
          const fullFieldObject = form?.formFields?.nodes.find(
            (field, index) => {
              return field?.id === fieldId
            }
          )

          let checkboxValues = []

          const choicesObject = fullFieldObject?.choices.map(
            (choice, index) => {
              return {
                id: index + 1,
                ...choice,
              }
            }
          )

          value = value ? value : []

          if (typeof value === "string") {
            value = [value]
          }

          value.forEach((singleValue) => {
            const mappedValue = choicesObject?.find((choice, index) => {
              return choice?.value === singleValue
            })

            checkboxValues.push({
              inputId: parseFloat(`${fieldId}.${mappedValue?.id}`),
              value: mappedValue?.value,
            })
          })
          formattedData.push({
            id: fieldId,
            checkboxValues: checkboxValues,
          })
          break
        default:
      }
    })
    setFormData(formattedData)
  }

  useEffect(() => {
    // setFormData is async
    if (formData) {
      mutateFunction()
    }
  }, [formData, mutateFunction])

  // Scroll to errors
  // https://github.com/react-hook-form/react-hook-form/issues/612

  useEffect(() => {
    // NOTE: kinda works, perhaps some issue with the simultaneous focusing?
    const elements = Object.keys(errors)
      .map((name) => document.getElementsByName(name)[0])
      .filter((el) => !!el)

    elements.sort((a, b) => b.scrollHeight - a.scrollHeight)
    elements[0]?.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [errors])

  // Map fields

  const fields = form?.formFields?.nodes
    .filter((field) => {
      return (
        field.visibility === "VISIBLE" || field.type?.toLowerCase() === "html"
      )
    })
    .map((field, index) => {
      let element = ""
      let fieldID = `field_${formId}_${field.id}`
      field.type = field.type?.toLowerCase()

      switch (field.type) {
        case "html":
          element = (
            <div
              key={index}
              className="form-group form-group-html"
              dangerouslySetInnerHTML={{ __html: field.content }}
            ></div>
          )
          break
        case "text":
        case "email":
        case "number":
          element = (
            <FormFieldText
              key={index}
              id={fieldID}
              type={field.type}
              name={fieldID}
              label={field.label}
              placeholder={field.placeholder}
              defaultValue={field.defaultValue}
              required={field.isRequired}
              description={field.description}
              errorMsg={
                field.errorMessage ? field.errorMessage : t("This is required.")
              }
              isInvalid={!!errors[fieldID]}
              {...register(fieldID, { required: field.isRequired })}
            />
          )
          break
        case "textarea":
          element = (
            <FormFieldTextArea
              key={index}
              id={fieldID}
              name={fieldID}
              label={field.label}
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              defaultValue={field.defaultValue}
              required={field.isRequired}
              description={field.description}
              errorMsg={
                field.errorMessage ? field.errorMessage : t("This is required.")
              }
              isInvalid={!!errors[fieldID]}
              {...register(fieldID, { required: field.isRequired })}
            />
          )
          break
        case "fileupload":
          element = (
            <FormFieldFileUpload
              key={index}
              id={fieldID}
              name={fieldID}
              label={field.label}
              required={field.isRequired}
              description={field.description}
              errorMsg={
                field.errorMessage ? field.errorMessage : t("This is required.")
              }
              isInvalid={!!errors[fieldID]}
              {...register(fieldID, { required: field.isRequired })}
            />
          )
          break
        case "radio":
          element = (
            <FormFieldRadio
              key={index}
              id={fieldID}
              name={fieldID}
              type={field.type}
              label={field.label}
              options={field.choices}
              required={field.isRequired}
              description={field.description}
              errorMsg={
                field.errorMessage ? field.errorMessage : t("This is required.")
              }
              isInvalid={!!errors[fieldID]}
              {...register(fieldID, { required: field.isRequired })}
            />
          )
          break

        case "checkbox":
          element = (
            <FormFieldCheckbox
              key={index}
              id={fieldID}
              name={fieldID}
              type={field.type}
              label={field.label}
              options={field.choices}
              required={field.isRequired}
              description={field.description}
              errorMsg={
                field.errorMessage ? field.errorMessage : t("This is required.")
              }
              isInvalid={!!errors[fieldID]}
              {...register(fieldID, { required: field.isRequired })}
            />
          )

          break

        case "select":
          element = (
            <FormFieldSelect
              key={index}
              id={fieldID}
              name={fieldID}
              label={field.label}
              placeholder={field.placeholder}
              description={field.description}
              options={field.choices}
              //required={field.isRequired}
              native={true}
              isInvalid={!!errors[fieldID]}
              {...register(fieldID, { required: field.isRequired })}
              selectContext={SiteContext}
            />
          )
          // TODO: field.choices.isSelected
          break
        default:
      }

      let showField = true

      if (field.conditionalLogic) {
        const rules = field.conditionalLogic.rules.map((rule) => {
          const conditionalFieldID = `field_${formId}_${rule.fieldId}`

          const formValues =
            Object.entries(watch)?.length > 0 ? watch : initialValues

          if (
            rule.operator === "IS" &&
            rule.value === formValues[conditionalFieldID]
          ) {
            return true
          }

          if (
            rule.operator === "IS_NOT" &&
            rule.value !== formValues[conditionalFieldID]
          ) {
            return true
          }

          return false
        })

        const ruleBoolean =
          field.conditionalLogic.logicType === "ALL"
            ? rules.every((r) => r === true)
            : rules.some((r) => r === true)
        const visibilityBoolean =
          field.conditionalLogic.actionType === "SHOW" ? true : false

        if (ruleBoolean !== visibilityBoolean) {
          showField = false
        }
      }

      return !field.conditionalLogic || showField ? element : null
    })
    .filter(Boolean)

  // Success message, error message

  const [successModalVisible, setSuccessModalVisible] = useState()
  const [errorModalVisible, setErrorModalVisible] = useState()

  return (
    <section className="base-margin-top base-margin-bottom-xl max-vvw text-align-left">
      <Form
        className="form max-wide no-margins"
        onSubmit={handleSubmit(onSubmit)}
      >
        {fields}
        <div className="base-margin-top-xs">
          <Button
            type="submit"
            variant="primary"
            size="medium-full-width"
            submitting={serverState.submitting}
          >
            {t("Submit")}
          </Button>
        </div>
      </Form>
      {confirmation?.message && (
        <ModalWindow
          open={successModalVisible}
          setOpen={setSuccessModalVisible}
          label={t("Success")}
        >
          <div className="text-align-center padding">
            <div
              dangerouslySetInnerHTML={{ __html: confirmation.message }}
            ></div>
            <div className="base-margin-top-xs">
              <Button
                type="submit"
                variant="secondary"
                size="medium-full-width"
                onClick={() => setSuccessModalVisible(false)}
              >
                {t("Close")}
              </Button>
            </div>
          </div>
        </ModalWindow>
      )}
      <ModalWindow
        open={errorModalVisible}
        setOpen={setErrorModalVisible}
        label={t("Error")}
      >
        <div className="text-align-center padding">
          <div>
            <h2>{t("Submission failed")}</h2>
            <p>
              {t(
                "There was an error while submitting the form. Please try again or contact us in another way."
              )}
            </p>
          </div>
          <div className="base-margin-top-xs">
            <Button
              type="submit"
              variant="secondary"
              size="medium-full-width"
              onClick={() => setErrorModalVisible(false)}
            >
              {t("Close")}
            </Button>
          </div>
        </div>
      </ModalWindow>
    </section>
  )
}

export const query = graphql`
  fragment GravityFormsForm on WpBlockGravityFormsForm {
    __typename
    formId
  }
`
export default GravityFormsForm
